/**
 * The authorization algebra: indices over the manifest, level comparison, and
 * effective-access calculation.
 *
 * This is the ONLY place a level is turned into a set of actions, and the only
 * place roles are combined. Both the API guards and the frontend import it, so
 * a decision cannot be computed two different ways.
 *
 * Combination rules (specification §3):
 *   - effective module level = the HIGHEST level any enabled role grants;
 *   - special actions are unioned INDEPENDENTLY of the level;
 *   - `none` is the absence of a grant, never a deny, so it can never lower
 *     what another role granted.
 *
 * Baseline rule (specification §4): a special action requires its module
 * baseline. Enforced here, at resolution time, so a grant stored before a level
 * was lowered becomes inert rather than exploitable. Save-time validation is a
 * second, independent check — not a substitute.
 */
import { z } from "zod";
import type {
  AccessLevel,
  ActionKey,
  BaselineLevel,
  EffectiveAccess,
  ModuleDefinition,
  ModuleDescriptor,
  ModuleGrant,
  ModuleKey,
} from "@yazamco/shared-types";
import { MODULE_MANIFEST, SPECIAL_ACTION_IMPLIES } from "./authz-manifest.js";
import { assertManifestValid } from "./authz-validate.js";

// The manifest is validated at import: an invalid manifest must not be loadable
// by anything, in any environment, ever.
assertManifestValid(MODULE_MANIFEST);

export { MODULE_MANIFEST, SPECIAL_ACTION_IMPLIES } from "./authz-manifest.js";
export {
  assertManifestValid,
  validateManifest,
  ManifestError,
} from "./authz-validate.js";
export type { ManifestActionKey, ManifestModuleKey } from "./authz-manifest.js";

// ── Ordering ─────────────────────────────────────────────────────────────────

export const ACCESS_LEVELS: readonly AccessLevel[] = [
  "none",
  "view",
  "edit",
  "full",
];

const LEVEL_RANK: Record<AccessLevel, number> = {
  none: 0,
  view: 1,
  edit: 2,
  full: 3,
};

/** True when `held` is at least `required`. */
export function levelSatisfies(
  held: AccessLevel,
  required: AccessLevel,
): boolean {
  return LEVEL_RANK[held] >= LEVEL_RANK[required];
}

/** The higher of two levels — the union rule for multiple roles. */
export function higherLevel(a: AccessLevel, b: AccessLevel): AccessLevel {
  return LEVEL_RANK[a] >= LEVEL_RANK[b] ? a : b;
}

export const accessLevelSchema = z.enum(["none", "view", "edit", "full"]);

// ── Indices ──────────────────────────────────────────────────────────────────

const MODULE_BY_KEY = new Map<ModuleKey, ModuleDefinition>(
  MODULE_MANIFEST.map((m) => [m.key, m as ModuleDefinition]),
);

/** action key → the module that declares it. */
const MODULE_BY_ACTION = new Map<ActionKey, ModuleDefinition>();
/** action key → its baseline, when the action is special. */
const SPECIAL_BASELINE = new Map<ActionKey, BaselineLevel>();
/** action key → the LOWEST level that grants it, when it is level-reachable. */
const ACTION_MIN_LEVEL = new Map<ActionKey, AccessLevel>();

for (const m of MODULE_MANIFEST) {
  const def = m as ModuleDefinition;
  for (const a of m.actions) MODULE_BY_ACTION.set(a, def);
  for (const s of m.special_actions) SPECIAL_BASELINE.set(s.key, s.requires_level);
  // Walk low → high so the first level that contains an action wins.
  for (const level of ["view", "edit", "full"] as const) {
    for (const a of m.access_levels[level]) {
      if (!ACTION_MIN_LEVEL.has(a)) ACTION_MIN_LEVEL.set(a, level);
    }
  }
}

export const MODULE_KEYS: readonly ModuleKey[] = MODULE_MANIFEST.map((m) => m.key);

export const ALL_ACTIONS: readonly ActionKey[] = [...MODULE_BY_ACTION.keys()];

export const ALL_SPECIAL_ACTIONS: readonly ActionKey[] = [
  ...SPECIAL_BASELINE.keys(),
];

export function getModule(key: ModuleKey): ModuleDefinition | undefined {
  return MODULE_BY_KEY.get(key);
}

export function moduleForAction(action: ActionKey): ModuleDefinition | undefined {
  return MODULE_BY_ACTION.get(action);
}

export function isKnownModule(key: string): boolean {
  return MODULE_BY_KEY.has(key);
}

export function isKnownAction(action: string): boolean {
  return MODULE_BY_ACTION.has(action);
}

export function isSpecialAction(action: string): boolean {
  return SPECIAL_BASELINE.has(action);
}

/** The baseline a special action requires, or undefined if it is not special. */
export function specialActionBaseline(
  action: string,
): BaselineLevel | undefined {
  return SPECIAL_BASELINE.get(action);
}

/** The lowest level granting `action`, or undefined for special actions. */
export function minimumLevelFor(action: string): AccessLevel | undefined {
  return ACTION_MIN_LEVEL.get(action);
}

/** Actions a level grants on a module (`none` grants nothing). */
export function actionsForLevel(
  module: ModuleKey,
  level: AccessLevel,
): readonly ActionKey[] {
  if (level === "none") return [];
  return MODULE_BY_KEY.get(module)?.access_levels[level] ?? [];
}

/** The manifest as the frontend consumes it (no internal action lists). */
export function moduleDescriptors(): ModuleDescriptor[] {
  return MODULE_MANIFEST.map((m) => ({
    key: m.key,
    displayName: m.display_name,
    category: m.category,
    description: m.description,
    levels: [...ACCESS_LEVELS],
    specialActions: m.special_actions.map((s) => ({
      key: s.key,
      label: s.label,
      description: s.description,
      requiresLevel: s.requires_level,
    })),
  }));
}

/** Module categories, in manifest order, for grouping the role editor. */
export function moduleCategories(): string[] {
  const seen: string[] = [];
  for (const m of MODULE_MANIFEST) {
    if (!seen.includes(m.category)) seen.push(m.category);
  }
  return seen;
}

// ── Grant validation (role save time) ────────────────────────────────────────

export interface GrantProblem {
  module: ModuleKey;
  action?: ActionKey;
  message: string;
}

/**
 * Validates a role's grants before they are stored.
 *
 * The rule worth stating plainly: a special action on a module set to `No Access`
 * is rejected here AND denied at runtime. Save-time validation keeps impossible
 * grants out of the database; runtime enforcement is what actually protects the
 * endpoint, because a level can be lowered after a grant was stored.
 */
export function validateGrants(
  grants: readonly ModuleGrant[],
): GrantProblem[] {
  const problems: GrantProblem[] = [];
  const seen = new Set<ModuleKey>();

  for (const g of grants) {
    const module = MODULE_BY_KEY.get(g.module);
    if (!module) {
      problems.push({ module: g.module, message: `unknown module '${g.module}'` });
      continue;
    }
    if (seen.has(g.module)) {
      problems.push({ module: g.module, message: `duplicate grant for '${g.module}'` });
    }
    seen.add(g.module);

    if (!ACCESS_LEVELS.includes(g.level)) {
      problems.push({ module: g.module, message: `unknown access level '${g.level}'` });
      continue;
    }

    for (const action of g.specialActions) {
      const baseline = SPECIAL_BASELINE.get(action);
      if (baseline === undefined) {
        problems.push({
          module: g.module,
          action,
          message: `'${action}' is not a special action`,
        });
        continue;
      }
      if (moduleForAction(action)?.key !== g.module) {
        problems.push({
          module: g.module,
          action,
          message: `'${action}' does not belong to module '${g.module}'`,
        });
        continue;
      }
      if (g.level === "none") {
        problems.push({
          module: g.module,
          action,
          message:
            `cannot grant the special action '${action}' on '${module.display_name}' ` +
            `while its access level is No Access — a special action adds to module ` +
            `access, it never substitutes for it`,
        });
        continue;
      }
      if (!levelSatisfies(g.level, baseline)) {
        problems.push({
          module: g.module,
          action,
          message:
            `the special action '${action}' requires at least '${baseline}' on ` +
            `'${module.display_name}', but the role grants '${g.level}'`,
        });
      }
    }
  }

  return problems;
}

// ── Effective access ─────────────────────────────────────────────────────────

/** A role as the resolver sees it. */
export interface ResolvedRole {
  id: string;
  key: string;
  name: string;
  grants: readonly ModuleGrant[];
}

/**
 * Combines every enabled role into one effective access set.
 *
 * `isSuperAdmin` is passed in, never inferred: it is an explicit assignment of
 * the built-in unrestricted role. A super administrator still gets a fully
 * populated `levels`/`actions` set (every module at `full`, every special
 * action) rather than a bypass branch in the guards — so there is exactly one
 * allow path in the system.
 */
export function computeEffectiveAccess(
  roles: readonly ResolvedRole[],
  options: { isSuperAdmin?: boolean } = {},
): EffectiveAccess {
  if (options.isSuperAdmin) return superAdminAccess();

  const levels: Record<ModuleKey, AccessLevel> = {};
  const grantedSpecials = new Set<ActionKey>();

  for (const role of roles) {
    for (const g of role.grants) {
      if (!MODULE_BY_KEY.has(g.module)) continue; // drift: ignore, never widen
      const current = levels[g.module] ?? "none";
      levels[g.module] = higherLevel(current, g.level);
      for (const a of g.specialActions) {
        // Only real special actions of that module can be collected.
        if (SPECIAL_BASELINE.has(a) && moduleForAction(a)?.key === g.module) {
          grantedSpecials.add(a);
        }
      }
    }
  }

  // Declared implications, applied BEFORE the baseline filter so an implied
  // action is held on exactly the terms a directly granted one would be: it
  // still needs its own baseline level, and it shows as inert when that is
  // missing. See SPECIAL_ACTION_IMPLIES — holding "manage business
  // integrations" without the matching read would mean editing a catalog you
  // cannot list.
  for (const granted of [...grantedSpecials]) {
    for (const implied of SPECIAL_ACTION_IMPLIES[granted] ?? []) {
      if (
        SPECIAL_BASELINE.has(implied) &&
        moduleForAction(implied)?.key === moduleForAction(granted)?.key
      ) {
        grantedSpecials.add(implied as ActionKey);
      }
    }
  }

  // Baseline filter: drop special actions whose module level is insufficient.
  // The dropped ones are kept separately — see `inertSpecialActions`.
  const specialActions: ActionKey[] = [];
  const inertSpecialActions: ActionKey[] = [];
  for (const a of grantedSpecials) {
    const baseline = SPECIAL_BASELINE.get(a);
    const module = moduleForAction(a);
    if (!baseline || !module) continue;
    const held = levels[module.key] ?? "none";
    if (held !== "none" && levelSatisfies(held, baseline)) specialActions.push(a);
    else inertSpecialActions.push(a);
  }

  // Flatten to a single action set so a guard decision is one lookup.
  const actions = new Set<ActionKey>();
  for (const [module, level] of Object.entries(levels)) {
    for (const a of actionsForLevel(module, level)) actions.add(a);
  }
  for (const a of specialActions) actions.add(a);

  return {
    levels,
    specialActions: specialActions.sort(),
    inertSpecialActions: inertSpecialActions.sort(),
    actions: [...actions].sort(),
    isSuperAdmin: false,
  };
}

/** Every module at `full`, every special action. */
export function superAdminAccess(): EffectiveAccess {
  const levels: Record<ModuleKey, AccessLevel> = {};
  const actions = new Set<ActionKey>();
  for (const m of MODULE_MANIFEST) {
    levels[m.key] = "full";
    for (const a of m.actions) actions.add(a);
  }
  return {
    levels,
    specialActions: [...ALL_SPECIAL_ACTIONS].sort(),
    actions: [...actions].sort(),
    isSuperAdmin: true,
  };
}

/** An access set granting nothing — the default-deny starting point. */
export function emptyAccess(): EffectiveAccess {
  return { levels: {}, specialActions: [], actions: [], isSuperAdmin: false };
}

export function levelFor(
  access: EffectiveAccess,
  module: ModuleKey,
): AccessLevel {
  return access.levels[module] ?? "none";
}

/**
 * THE authorization decision. Deliberately the only function that answers it.
 *
 * Both checks are required for a special action, and the module check is not
 * redundant: it is what closes the path where a hidden module is extracted
 * through a granted export or bulk operation.
 */
export function can(access: EffectiveAccess, action: ActionKey): boolean {
  const module = MODULE_BY_ACTION.get(action);
  if (!module) return false; // unknown action → deny (default deny)

  const held = levelFor(access, module.key);
  if (held === "none") return false; // no module access → nothing on it

  const baseline = SPECIAL_BASELINE.get(action);
  if (baseline !== undefined) {
    if (!levelSatisfies(held, baseline)) return false;
    return access.specialActions.includes(action);
  }

  const required = ACTION_MIN_LEVEL.get(action);
  if (!required) return false; // declared but unreachable → deny
  if (!levelSatisfies(held, required)) return false;

  // Exact-grant principals (services) must hold the action literally: a level
  // gates the module for them, but never widens what they may do inside it.
  if (access.exact) return access.actions.includes(action);
  return true;
}

/** Whether the principal can see the module at all (View or better). */
export function canViewModule(
  access: EffectiveAccess,
  module: ModuleKey,
): boolean {
  return levelFor(access, module) !== "none";
}

// ── Grant serialization (API + DB boundary) ──────────────────────────────────

export const moduleGrantSchema = z
  .object({
    module: z.string().refine(isKnownModule, "unknown module"),
    level: accessLevelSchema,
    specialActions: z
      .array(z.string().refine(isSpecialAction, "unknown special action"))
      .default([]),
  })
  .strict();

export const moduleGrantsSchema = z
  .array(moduleGrantSchema)
  .max(MODULE_MANIFEST.length)
  .superRefine((grants, ctx) => {
    for (const p of validateGrants(grants)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: p.message,
        path: p.action ? [p.module, p.action] : [p.module],
      });
    }
  });

export type ModuleGrantInput = z.infer<typeof moduleGrantSchema>;

/** PUT /roles/:id/grants body. */
export const setRoleGrantsSchema = z
  .object({
    grants: moduleGrantsSchema,
    /** Optimistic concurrency: the version the client last read. */
    version: z.number().int().nonnegative(),
  })
  .strict();

export type SetRoleGrantsInput = z.infer<typeof setRoleGrantsSchema>;

/** PUT /settings/authz-mode body. `null` clears the override. */
export const setAuthzModeSchema = z
  .object({ mode: z.enum(["shadow", "enforce"]).nullable() })
  .strict();
